import React, { useMemo } from "react";

import type { AdminBusinessSummary } from "../../lib/api/adminBusinessSummary";
import { formatAmount } from "../../utils/FormatPerson";

const CURRENCY_COLORS = [
  "#0D851D",
  "#BE6B0A",
  "#8B5CF6",
  "#279DF3",
  "#D92B2B",
  "#4E606E",
  "#EAB308",
  "#EC4899",
];

export function businessStatAccent(key: string): { bar: string; glow: string } {
  if (key === "approved" || key === "verified" || key === "completed") {
    return { bar: "#03C668", glow: "rgba(3, 198, 104, 0.12)" };
  }
  if (key === "pending") {
    return { bar: "#F28A0F", glow: "rgba(242, 138, 15, 0.12)" };
  }
  if (key === "rejected" || key === "failed") {
    return { bar: "#DF4E4E", glow: "rgba(223, 78, 78, 0.12)" };
  }
  return { bar: "#0B75C2", glow: "rgba(11, 117, 194, 0.12)" };
}

type Props = {
  summary: AdminBusinessSummary;
  /**
   * `overview` shows portfolio-wide counts. `merchant` drops the business
   * count and onboarding mix, which are meaningless scoped to one business.
   */
  scope?: "overview" | "merchant";
};

const MerchantSummarySections: React.FC<Props> = ({
  summary,
  scope = "overview",
}) => {
  const isOverview = scope === "overview";

  const businessStats = useMemo(() => {
    const b = summary.businesses;
    return [
      { key: "approved", label: "Approved", value: b.approved },
      { key: "pending", label: "Pending", value: b.pending },
      { key: "rejected", label: "Rejected", value: b.rejected },
      { key: "verified", label: "Verified", value: b.verified },
    ];
  }, [summary.businesses]);

  const currencyRows = useMemo(() => {
    const items = summary.deposits.byCurrency ?? [];
    const total = items.reduce((s, c) => s + (Number(c.usdAmount) || 0), 0);
    return [...items]
      .sort((a, b) => (b.usdAmount || 0) - (a.usdAmount || 0))
      .map((c, i) => ({
        ...c,
        pct: total > 0 ? ((Number(c.usdAmount) || 0) / total) * 100 : 0,
        color: CURRENCY_COLORS[i % CURRENCY_COLORS.length],
      }));
  }, [summary.deposits.byCurrency]);

  const payoutBuckets = useMemo(() => {
    const p = summary.payouts;
    return [
      { key: "completed", label: "Completed", bucket: p.completed },
      { key: "pending", label: "Pending", bucket: p.pending },
      { key: "failed", label: "Failed", bucket: p.failed },
    ];
  }, [summary.payouts]);

  const kpis = [
    ...(isOverview
      ? [
          {
            label: "Businesses",
            value: summary.businesses.total.toLocaleString(),
            hint: "Registered merchants",
          },
        ]
      : []),
    {
      label: "Payment pages",
      value: summary.paymentPages.total.toLocaleString(),
      hint: isOverview ? "Created across merchants" : "Created by this merchant",
    },
    {
      label: "Deposit volume (USD)",
      value: `$${formatAmount(summary.deposits.totalUSD)}`,
      hint: `${summary.deposits.count.toLocaleString()} deposits`,
    },
    {
      label: "Payout volume (NGN)",
      value: `₦${formatAmount(summary.payouts.totalNGN)}`,
      hint: `${summary.payouts.count.toLocaleString()} payouts`,
    },
  ];

  return (
    <>
      <section
        className={`grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 ${
          isOverview ? "xl:grid-cols-4" : "xl:grid-cols-3"
        }`}
        aria-label="Key metrics"
      >
        {kpis.map((kpi) => (
          <div
            key={kpi.label}
            className="rounded-xl border border-[#2C363E] bg-gradient-to-b from-[#1C2227] to-[#1A1F24] px-4 py-4 shadow-[0_2px_12px_rgba(0,0,0,0.2)] hover:border-[#3A4852] transition-colors"
          >
            <p className="text-[11px] font-semibold text-[#869AA9] uppercase tracking-wider">
              {kpi.label}
            </p>
            <p className="font-sora text-xl sm:text-2xl font-bold text-white mt-2 tabular-nums tracking-tight">
              {kpi.value}
            </p>
            <p className="text-[11px] text-[#5C6B76] mt-1">{kpi.hint}</p>
          </div>
        ))}
      </section>

      {isOverview && (
        <section
          className="rounded-2xl border border-[#2C363E] bg-gradient-to-b from-[#1C2227] to-[#181D22] p-5 sm:p-7 mb-8 shadow-[0_8px_32px_rgba(0,0,0,0.28)]"
          aria-labelledby="merchants-onboarding-heading"
        >
          <div className="flex items-baseline justify-between gap-2 mb-5">
            <h2
              id="merchants-onboarding-heading"
              className="text-[11px] font-bold text-[#869AA9] uppercase tracking-widest"
            >
              Onboarding
            </h2>
            <span className="text-[11px] text-[#5C6B76]">
              {summary.businesses.total.toLocaleString()} businesses total
            </span>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {businessStats.map((stat) => {
              const { bar, glow } = businessStatAccent(stat.key);
              const pct =
                summary.businesses.total > 0
                  ? (stat.value / summary.businesses.total) * 100
                  : 0;
              return (
                <div
                  key={stat.key}
                  className="rounded-xl border border-[#2C363E] bg-[#1C2227] px-4 py-3.5 transition hover:border-[#3A4852]"
                  style={{
                    boxShadow: `inset 0 0 0 1px ${glow}`,
                    borderLeftWidth: 3,
                    borderLeftColor: bar,
                  }}
                >
                  <p className="text-[11px] text-[#869AA9] mb-1.5 leading-tight">
                    {stat.label}
                  </p>
                  <p className="font-sora text-xl font-bold text-white tabular-nums">
                    {stat.value.toLocaleString()}
                  </p>
                  <p className="text-[11px] text-[#5C6B76] mt-1 tabular-nums">
                    {pct.toFixed(1)}% of total
                  </p>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mb-10">
        <section aria-labelledby="merchants-deposits-heading">
          <div className="flex items-baseline justify-between gap-2 mb-4">
            <h2
              id="merchants-deposits-heading"
              className="text-[12px] font-bold text-[#869AA9] uppercase tracking-widest"
            >
              Deposits
            </h2>
            <span className="text-[11px] text-[#5C6B76]">
              ₦{formatAmount(summary.deposits.totalNGN)} · $
              {formatAmount(summary.deposits.totalUSD)}
            </span>
          </div>
          {currencyRows.length === 0 ? (
            <p className="text-[13px] text-[#869AA9] rounded-lg border border-dashed border-[#2C363E] bg-[#141920]/50 px-4 py-6 text-center">
              No deposits for this range.
            </p>
          ) : (
            <div className="space-y-3">
              {currencyRows.map((c) => (
                <div
                  key={c.currency}
                  className="rounded-xl border border-[#2C363E] bg-[#1C2227] p-4 transition hover:border-[#3A4852]"
                >
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <span className="text-[13px] font-semibold text-[#E7EBEE] uppercase">
                      {c.currency}
                    </span>
                    <span className="text-[13px] tabular-nums text-[#AEBCC6]">
                      <span className="font-sora font-bold text-white">
                        ${formatAmount(c.usdAmount)}
                      </span>{" "}
                      · {c.count.toLocaleString()}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-[#0D1114] overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500 ease-out"
                      style={{
                        width: `${Math.max(c.pct, 0)}%`,
                        backgroundColor: c.color,
                      }}
                    />
                  </div>
                  <p className="text-[11px] text-[#5C6B76] mt-2 tabular-nums">
                    {formatAmount(c.amount)} {c.currency.toUpperCase()} ·{" "}
                    {c.pct.toFixed(1)}% of USD volume
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>

        <section aria-labelledby="merchants-payouts-heading">
          <div className="flex items-baseline justify-between gap-2 mb-4">
            <h2
              id="merchants-payouts-heading"
              className="text-[12px] font-bold text-[#869AA9] uppercase tracking-widest"
            >
              Payouts
            </h2>
            <span className="text-[11px] text-[#5C6B76]">
              Unsettled: ₦{formatAmount(summary.payouts.unsettled)}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {payoutBuckets.map(({ key, label, bucket }) => {
              const { bar, glow } = businessStatAccent(key);
              return (
                <div
                  key={key}
                  className="rounded-xl border border-[#2C363E] bg-[#1C2227] px-4 py-3.5 transition hover:border-[#3A4852]"
                  style={{
                    boxShadow: `inset 0 0 0 1px ${glow}`,
                    borderLeftWidth: 3,
                    borderLeftColor: bar,
                  }}
                >
                  <p className="text-[11px] text-[#869AA9] mb-1.5 leading-tight">
                    {label}
                  </p>
                  <p className="font-sora text-xl font-bold text-white tabular-nums">
                    {bucket.count.toLocaleString()}
                  </p>
                  <p className="text-[12px] text-[#AEBCC6] mt-1 tabular-nums break-all">
                    ₦{formatAmount(bucket.amountNGN)}
                  </p>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </>
  );
};

export default MerchantSummarySections;
